import Button, { ButtonProps } from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { blue, green, red } from '@material-ui/core/colors';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { memo, useCallback } from 'react';
import { LoadingButtonState } from './loading-button-state';

// customized version of a sample in Material-UI page (https://material-ui.com/components/progress/#interactive-integration)

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center'
    },
    wrapper: {
      position: 'relative'
    },
    buttonSuccess: {
      backgroundColor: green[500],
      '&:hover': {
        backgroundColor: green[700]
      }
    },
    buttonError: {
      backgroundColor: red[500],
      '&:hover': {
        backgroundColor: red[700]
      }
    },
    buttonProgress: {
      color: blue[500],
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12
    }
  })
);

interface Props extends ButtonProps {
  label: string;
  status: LoadingButtonState['status'];
  onClick?: () => void;
}

export const ButtonWithLoadingCircle = memo(
  ({ label, status, disabled, onClick, ...props }: Props) => {
    const classes = useStyles();

    const buttonClassname = clsx({
      [classes.buttonSuccess]: status === 'success',
      [classes.buttonError]: status === 'error'
    });

    const handleButtonClick = useCallback(() => {
      if (status !== 'pending' && onClick !== undefined) {
        onClick();
      }
    }, [status, onClick]);

    return (
      <div className={classes.root}>
        <div className={classes.wrapper}>
          <Button
            variant='contained'
            color='primary'
            className={buttonClassname}
            disabled={status === 'pending' || disabled === true}
            onClick={handleButtonClick}
            {...props}
          >
            {label}
          </Button>
          {status === 'pending' ? (
            <CircularProgress size={24} className={classes.buttonProgress} />
          ) : (
            undefined
          )}
        </div>
      </div>
    );
  }
);

ButtonWithLoadingCircle.displayName = 'ButtonWithLoadingCircle';
